import { DEFAULT_AB_SPLIT_RATIO } from '../../../constants';
import { getStepTargets } from '../../../hooks/useStepGraph';
const WIDTH = 264;
const HEIGHT = 160;
const ROW_PITCH = 272;
const GAP = 80;
const PADDING = 48;
const FIRST_Y = 64;
const RETURN_LANE = 168;
const connectionKey = (source, branch) => JSON.stringify([source, branch]);
const branchOffset = (branch) => branch === 'on_true' || branch === 'armA'
    ? -56
    : branch === 'on_false' || branch === 'armB'
        ? 56
        : 0;
const branchLabel = (source, branch) => {
    if (branch === 'next')
        return 'Next';
    if (branch === 'on_true')
        return 'Yes';
    if (branch === 'on_false')
        return 'No';
    const ratio = source.type === 'ab_split' ? (source.ratio ?? DEFAULT_AB_SPLIT_RATIO) : 50;
    return `${branch === 'armA' ? 'A' : 'B'} · ${branch === 'armA' ? ratio : 100 - ratio}%`;
};
/** Positions the existing graph's layers; reachability and cycle semantics stay with useStepGraph. */
export const layoutJourney = (graph) => {
    const rows = graph.layers.map((steps) => steps.map((step) => ({
        key: step.id,
        kind: 'step',
        x: 0,
        y: 0,
        width: WIDTH,
        height: HEIGHT,
        step
    })));
    const byKey = new Map(rows.flat().map((node) => [node.key, node]));
    const stepKeys = new Set(byKey.keys());
    const rowByKey = new Map();
    rows.forEach((row, index) => row.forEach((node) => rowByKey.set(node.key, index)));
    const flags = new Map(graph.edges.map((edge) => [connectionKey(edge.from, edge.branch), edge]));
    const connections = [];
    // Iterate only original steps: terminal nodes never acquire outgoing connections.
    graph.layers.forEach((steps, rowIndex) => {
        for (const source of steps) {
            for (const { target, branch } of getStepTargets(source)) {
                const key = connectionKey(source.id, branch);
                const isExit = target === 'exit';
                let to = target;
                if (isExit || !stepKeys.has(target)) {
                    to = `terminal:${key}`;
                    while (byKey.has(to))
                        to = `terminal:${to}`;
                    const node = {
                        key: to,
                        kind: isExit ? 'finish' : 'missing',
                        x: 0,
                        y: 0,
                        width: WIDTH,
                        height: 76,
                        ...(isExit ? { reason: source.exit_reason } : {}),
                        target
                    };
                    (rows[rowIndex + 1] ??= []).push(node);
                    byKey.set(to, node);
                    rowByKey.set(to, rowIndex + 1);
                }
                const edge = flags.get(key);
                connections.push({
                    key,
                    from: source.id,
                    to,
                    branch,
                    label: branchLabel(source, branch),
                    target,
                    source,
                    isExit,
                    isLegalCycle: edge?.isLegalCycle ?? false,
                    isIllegalCycle: edge?.isIllegalCycle ?? false,
                    path: '',
                    labelX: 0,
                    labelY: 0
                });
            }
        }
    });
    const largestRow = Math.max(0, ...rows.map((row) => row.length));
    const contentWidth = Math.max(560, largestRow * WIDTH + Math.max(0, largestRow - 1) * GAP + PADDING * 2);
    const incoming = new Map();
    for (const edge of connections) {
        if (rowByKey.get(edge.from) < rowByKey.get(edge.to)) {
            const parents = incoming.get(edge.to) ?? [];
            parents.push(edge);
            incoming.set(edge.to, parents);
        }
    }
    rows.forEach((row, rowIndex) => {
        const desiredX = (node) => {
            const parents = incoming.get(node.key) ?? [];
            if (!parents.length)
                return contentWidth / 2;
            return (parents.reduce((sum, edge) => {
                const parent = byKey.get(edge.from);
                return sum + parent.x + WIDTH / 2 + branchOffset(edge.branch);
            }, 0) / parents.length);
        };
        // Stable sorting retains the graph's ordering when the parent positions tie.
        row.sort((a, b) => desiredX(a) - desiredX(b));
        const start = (contentWidth - (row.length * WIDTH + Math.max(0, row.length - 1) * GAP)) / 2;
        row.forEach((node, index) => {
            node.x = start + index * (WIDTH + GAP);
            node.y = FIRST_Y + rowIndex * ROW_PITCH;
        });
    });
    let returnCount = 0;
    for (const edge of connections) {
        const source = byKey.get(edge.from);
        const target = byKey.get(edge.to);
        const sx = source.x + WIDTH / 2 + branchOffset(edge.branch);
        const sy = source.y + source.height;
        if (rowByKey.get(edge.to) > rowByKey.get(edge.from)) {
            const tx = target.x + WIDTH / 2;
            const bendY = sy + (target.y - sy) / 2;
            edge.path = `M ${sx} ${sy} V ${bendY} H ${tx} V ${target.y}`;
            edge.labelX = sx;
            edge.labelY = bendY - 16;
        }
        else {
            const laneX = contentWidth - PADDING + GAP + returnCount * RETURN_LANE;
            returnCount += 1;
            const belowSource = sy + 48;
            const aboveTarget = target.y - 32;
            const approachX = target.x + target.width + GAP / 2;
            const targetY = target.y + target.height / 2;
            // Approach an interior target through the row gap, then its adjacent column gap.
            // A direct gutter-to-card line would cut through other cards in that row.
            edge.path = `M ${sx} ${sy} V ${belowSource} H ${laneX} V ${aboveTarget} H ${approachX} V ${targetY} H ${target.x + target.width}`;
            edge.labelX = laneX + 76;
            edge.labelY = belowSource;
        }
    }
    const nodes = rows.flat();
    return {
        width: contentWidth + (returnCount ? GAP + returnCount * RETURN_LANE : 0),
        height: Math.max(160, ...nodes.map((node) => node.y + node.height + PADDING), ...connections.map((edge) => edge.labelY + 40 + PADDING)),
        nodes,
        connections
    };
};
